import type { Pasco } from "@src/domain/pascos/entities/pasco.entity";
import type { PascoDTO, PascoListItemDTO } from "../dto";

/**
 * PASCO DTO Mapper
 * Responsible for converting between domain entities and DTOs
 * This ensures domain entities never leak into the presentation layer
 */
export class PascoDtoMapper {
    /**
     * Convert a domain Pasco entity to a PascoDTO
     * @param pasco - Domain entity
     * @returns PascoDTO for presentation layer
     */
    static toDTO(pasco: Pasco): PascoDTO {
        return {
            pascoId: pasco.pascoId,
            path: pasco.path,
            courseCode: pasco.courseCode,
            courseName: pasco.courseName,
            academicYear: pasco.academicYear,
            semester: pasco.semester,
            level: pasco.level,
            isApproved: pasco.isApproved,
            isReviewed: pasco.isReviewed,
            isInterimAssessment: pasco.isInterimAssessment,
            isSupplementary: pasco.isSupplementary,
            processingStatus: pasco.processingStatus,
            courseDescription: pasco.courseDescription,
            department: pasco.department,
            universityId: pasco.universityId,
            universityName: '', // TODO: Fetch university name from repository
            uploadedBy: pasco.uploadedBy ? {
                userId: pasco.uploadedBy.userId,
                name: pasco.uploadedBy.name,
                profilePicture: pasco.uploadedBy.profilePicture,
            } : undefined,
            createdAt: pasco.createdAt.toISOString(),
            updatedAt: pasco.updatedAt.toISOString(),
        };
    }

    /**
     * Convert a domain Pasco entity to a simplified PascoListItemDTO
     * @param pasco - Domain entity
     * @returns PascoListItemDTO for list views
     */
    static toListItemDTO(pasco: Pasco): PascoListItemDTO {
        return {
            pascoId: pasco.pascoId,
            courseCode: pasco.courseCode,
            courseName: pasco.courseName,
            academicYear: pasco.academicYear,
            semester: pasco.semester,
            level: pasco.level,
            isApproved: pasco.isApproved,
            isReviewed: pasco.isReviewed,
            isSupplementary: pasco.isSupplementary,
            processingStatus: pasco.processingStatus,
            universityId: pasco.universityId,
            universityName: '', // TODO: Fetch university name from repository
            uploadedBy: pasco.uploadedBy ? {
                userId: pasco.uploadedBy.userId,
                name: pasco.uploadedBy.name,
                profilePicture: pasco.uploadedBy.profilePicture,
            } : undefined,
            createdAt: pasco.createdAt.toISOString(),
        };
    }

    /**
     * Convert an array of domain Pasco entities to DTOs
     * @param pascos - Array of domain entities
     * @returns Array of PascoDTOs
     */
    static toDTOArray(pascos: Pasco[]): PascoDTO[] {
        return pascos.map(pasco => this.toDTO(pasco));
    }

    /**
     * Convert an array of domain Pasco entities to list item DTOs
     * @param pascos - Array of domain entities
     * @returns Array of PascoListItemDTOs
     */
    static toListItemDTOArray(pascos: Pasco[]): PascoListItemDTO[] {
        return pascos.map(pasco => this.toListItemDTO(pasco));
    }
}
